/**
 * Per 2.11: "The client must degrade gracefully if the manifest is
 * unreachable — a missing or unfetchable manifest must never block the
 * game's boot sequence from reaching the login screen; art is allowed to
 * be missing, a permanently-stuck client is not. This is a hard
 * requirement on BootScene/AssetLoader, verified by deliberately testing
 * with an unreachable VITE_ASSET_CDN before every release."
 */
export interface AssetManifest {
  // shared via packages/shared-types per 2.11 — client keeps its own copy
  // of the shape it actually consumes.
  version: string;
  assets: Record<string, { type: "image" | "spritesheet" | "audio"; path: string; frameWidth?: number; frameHeight?: number }>;
}

export type ManifestLoadResult =
  | { ok: true; manifest: AssetManifest }
  | { ok: false; reason: "unreachable" | "invalid_json" | "timeout" };

const MANIFEST_FETCH_TIMEOUT_MS = 5000;

export class AssetLoader {
  constructor(
    assetCdn: string,
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args)
  ) {
    this.assetCdn = assetCdn.replace(/\/+$/, "");
  }

  private readonly assetCdn: string;

  /**
   * Never throws. Every failure mode collapses to a typed
   * ManifestLoadResult so BootScene can proceed to the login screen with
   * missing art rather than getting stuck — this is the single most
   * important behavior in this file per 2.11's hard requirement.
   */
  async loadManifest(): Promise<ManifestLoadResult> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), MANIFEST_FETCH_TIMEOUT_MS);

    try {
      const res = await this.fetchImpl(`${this.assetCdn}/manifest.json`, { signal: controller.signal });
      if (!res.ok) {
        return { ok: false, reason: "unreachable" };
      }
      let json: unknown;
      try {
        json = await res.json();
      } catch {
        return { ok: false, reason: "invalid_json" };
      }
      if (!isValidManifest(json)) {
        return { ok: false, reason: "invalid_json" };
      }
      return { ok: true, manifest: json };
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return { ok: false, reason: "timeout" };
      }
      return { ok: false, reason: "unreachable" };
    } finally {
      clearTimeout(timeout);
    }
  }

  assetUrl(relativePath: string): string {
    return `${this.assetCdn}/${relativePath.replace(/^\/+/, "")}`;
  }
}

function isValidManifest(value: unknown): value is AssetManifest {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.version === "string" && typeof v.assets === "object" && v.assets !== null;
}
